/**
 * Hook to track container size changes using ResizeObserver.
 * Returns current dimensions and provides a ref to attach to the container.
 */

import { useCallback, useEffect, useRef, useState } from "react";

export interface Dimensions {
  width: number;
  height: number;
}

/**
 * Observe element size changes and return current dimensions.
 * Uses ResizeObserver for efficient native observation.
 *
 * @param initialWidth - Initial width (default: 0)
 * @param initialHeight - Initial height (default: 0)
 * @returns Tuple of [ref to attach, current dimensions]
 *
 * @example
 * ```tsx
 * const MyChart = () => {
 *   const [containerRef, { width, height }] = useResizeObserver();
 *
 *   return (
 *     <div ref={containerRef}>
 *       <svg width={width} height={height}>
 *         {/* chart content *\/}
 *       </svg>
 *     </div>
 *   );
 * };
 * ```
 */
export function useResizeObserver(
  initialWidth = 0,
  initialHeight = 0
): [React.RefObject<HTMLDivElement>, Dimensions] {
  const ref = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState<Dimensions>({
    width: initialWidth,
    height: initialHeight,
  });

  const handleResize = useCallback((entries: ResizeObserverEntry[]) => {
    if (!entries[0]) return;

    const { width, height } = entries[0].contentRect;

    setDimensions((prev) => {
      if (prev.width === width && prev.height === height) {
        return prev;
      }

      return { width, height };
    });
  }, []);

  useEffect(() => {
    const element = ref.current;

    if (!element) {
      return undefined;
    }

    const observer = new ResizeObserver(handleResize);

    observer.observe(element);

    const { width, height } = element.getBoundingClientRect();

    setDimensions({ width, height });

    return () => {
      observer.disconnect();
    };
  }, [handleResize]);

  return [ref, dimensions];
}
